<template>
  <div class="modal-overlay">
    <div class="modal">
      <div class="modal-header">
        <h2>{{ $t('bo.translations.create.title') }}</h2>
        <button @click="$emit('close')" class="btn-close">&times;</button>
      </div>

      <!-- Success state (shown after submit when FR warning exists) -->
      <template v-if="submitted">
        <div class="modal-body">
          <div class="success-message">
            ✅ {{ $t('bo.translations.create.success') }}
          </div>
          <div v-if="warning" class="warning-message">
            ⚠️ {{ warning }}
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" @click="$emit('created')" class="btn-primary">
            {{ $t('bo.common.done') }}
          </button>
        </div>
      </template>

      <form v-else @submit.prevent="submit" class="modal-form">
        <div class="modal-body">
          <!-- Namespace -->
          <div class="form-group">
            <label>{{ $t('bo.translations.create.namespace') }}</label>
            <input
              v-model="form.namespace"
              list="ns-create-list"
              type="text"
              required
              :placeholder="$t('bo.translations.create.namespacePlaceholder')"
            />
            <datalist id="ns-create-list">
              <option v-for="ns in namespaces" :key="ns" :value="ns" />
            </datalist>
          </div>

          <!-- EN Value -->
          <div class="form-group">
            <label>{{ $t('bo.translations.create.enValue') }}</label>
            <textarea
              v-model="form.en_value"
              rows="3"
              required
              :placeholder="$t('bo.translations.create.enValuePlaceholder')"
            ></textarea>
          </div>

          <!-- Key (auto-suggestion) -->
          <div class="form-group">
            <label>{{ $t('bo.translations.create.key') }}</label>
            <div class="key-input-wrapper">
              <input
                v-model="form.key"
                type="text"
                required
                pattern="[a-z0-9_.]+"
                :maxlength="MAX_KEY_LENGTH"
                :placeholder="$t('bo.translations.create.keyPlaceholder')"
                :class="{ 'input-suggestion': isSuggestion }"
                @input="isSuggestion = false"
              />
              <span v-if="loadingSuggestion" class="key-hint">{{ $t('bo.translations.create.generating') }}</span>
              <span v-else-if="isSuggestion" class="key-hint suggested">✨ {{ $t('bo.translations.create.autoSuggested') }}</span>
            </div>
            <small>{{ $t('bo.translations.create.keyHint', { max: MAX_KEY_LENGTH }) }}</small>
          </div>

          <!-- Description -->
          <div class="form-group">
            <label>{{ $t('bo.common.description') }}</label>
            <textarea
              v-model="form.description"
              rows="2"
              :placeholder="$t('bo.translations.create.descPlaceholder')"
            ></textarea>
          </div>

          <!-- FR Value -->
          <div class="form-group">
            <label>{{ $t('bo.translations.create.frValue') }}</label>
            <textarea
              v-model="form.fr_value"
              rows="3"
              :placeholder="$t('bo.translations.create.frValuePlaceholder')"
            ></textarea>
          </div>

          <!-- Auto-translate -->
          <div class="form-group">
            <label class="checkbox">
              <input
                type="checkbox"
                v-model="autoTranslateFr"
              />
              {{ $t('bo.translations.create.autoTranslate') }}
            </label>
          </div>

          <div v-if="error" class="error-message">
            {{ error }}
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" @click="$emit('close')" class="btn-secondary">
            {{ $t('bo.common.cancel') }}
          </button>
          <button type="submit" :disabled="loading" class="btn-primary">
            {{ loading ? $t('bo.common.creating') : $t('bo.common.create') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import translationService from '@/services/translationService'

const { t } = useI18n()

const emit = defineEmits(['close', 'created'])

const namespaces = ref([])

onMounted(async () => {
  try {
    const data = await translationService.getNamespaces()
    namespaces.value = data
  } catch (err) {
    console.error('Failed to load namespaces:', err)
  }
})

const form = ref({
  key: '',
  namespace: '',
  description: '',
  en_value: '',
  fr_value: '',
})

// Séparé du form — évite tout conflit avec v-model
const autoTranslateFr = ref(true)

const loading = ref(false)
const loadingSuggestion = ref(false)
const error = ref('')
const warning = ref('')
const submitted = ref(false)
const isSuggestion = ref(false)

// Longueur max alignee sur TranslationKey::MAX_KEY_LENGTH (backend).
const MAX_KEY_LENGTH = 30

// Tronque proprement une cle : coupe a la derniere separation (. ou _) si possible,
// sinon coupe net, et retire tout separateur en fin.
const capKey = (key) => {
  if (!key || key.length <= MAX_KEY_LENGTH) return key
  let truncated = key.slice(0, MAX_KEY_LENGTH)
  const lastSep = Math.max(truncated.lastIndexOf('_'), truncated.lastIndexOf('.'))
  if (lastSep > MAX_KEY_LENGTH * 0.5) {
    truncated = truncated.slice(0, lastSep)
  }
  return truncated.replace(/[._]+$/, '')
}

// Décoche si FR rempli, recoche si FR vidé
watch(() => form.value.fr_value, (val) => {
  autoTranslateFr.value = !val
})

// Suggestion auto quand namespace ou en_value change
let debounceTimer = null
watch(
  () => [form.value.namespace, form.value.en_value],
  () => {
    clearTimeout(debounceTimer)
    debounceTimer = setTimeout(fetchSuggestion, 400)
  }
)

const fetchSuggestion = async () => {
  if (!form.value.namespace || !form.value.en_value) return

  loadingSuggestion.value = true
  try {
    const data = await translationService.suggestKey({
      namespace: form.value.namespace,
      en_value: form.value.en_value,
    })
    form.value.key = capKey(data.suggested_key)
    isSuggestion.value = true
  } catch (err) {
    console.error('Suggestion error:', err)
  } finally {
    loadingSuggestion.value = false
  }
}

const submit = async () => {
  loading.value = true
  error.value = ''
  warning.value = ''

  try {
    const created = await translationService.createKey({
      ...form.value,
      auto_translate_fr: autoTranslateFr.value,
    })

    // Check if FR auto-translate was requested but not created
    const wantedFR = autoTranslateFr.value && !form.value.fr_value
    const hasFR = created.translations?.some(t => t.locale?.code === 'fr')

    if (wantedFR && !hasFR) {
      warning.value = t('bo.translations.create.warningFrFailed')
      submitted.value = true
    } else {
      emit('created')
    }
  } catch (err) {
    if (err.response?.status === 422) {
      const errors = err.response.data.errors
      error.value = Object.values(errors).flat().join(' ')
    } else {
      error.value = err.response?.data?.message || t('bo.translations.create.errorCreate')
    }
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: white;
  border-radius: 0.5rem;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.25rem;
  color: #1f2937;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  line-height: 1;
}

.modal-form { display: flex; flex-direction: column; flex: 1; overflow: hidden; }
.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #374151;
}

.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background: white;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-group small {
  display: block;
  color: #6b7280;
  margin-top: 0.25rem;
  font-size: 0.75rem;
}

.key-input-wrapper {
  position: relative;
}

.key-input-wrapper input {
  padding-right: 9rem;
}

.input-suggestion {
  border-color: #10b981 !important;
  background: #f0fdf4 !important;
}

.key-hint {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.75rem;
  color: #6b7280;
  pointer-events: none;
}

.key-hint.suggested {
  color: #10b981;
}

.checkbox {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: normal !important;
  cursor: pointer;
}

.checkbox input[type="checkbox"] {
  width: auto;
  cursor: pointer;
}

.checkbox input[type="checkbox"] {
  width: auto !important;
  padding: 0 !important;
  border: none !important;
  background: transparent !important;
  appearance: auto !important;
  -webkit-appearance: checkbox !important;
  cursor: pointer;
}

.success-message {
  padding: 0.75rem;
  background: #dcfce7;
  color: #166534;
  border-radius: 0.375rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  font-weight: 500;
}

.warning-message {
  padding: 0.75rem;
  background: #fef9c3;
  color: #854d0e;
  border-radius: 0.375rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
}

.error-message {
  padding: 0.75rem;
  background: #fee2e2;
  color: #991b1b;
  border-radius: 0.375rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.btn-primary,
.btn-secondary {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
}

.btn-secondary {
  background: #e5e7eb;
  color: #374151;
}

.btn-secondary:hover {
  background: #d1d5db;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>